import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { authAPI, referralAPI } from '../services/api';
import { trackPurchaseConversion } from '../utils/conversionTracking';

const TopUpPage: React.FC = () => {
  const { user, fetchUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [gbAmount, setGbAmount] = useState<number>(1000); // Default to 1TB (Growth tier)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCodeModal, setShowCodeModal] = useState(false);

  // Top-up code states (legacy redeem code modal)
  const [code, setCode] = useState('');
  const [isLoadingCode, setIsLoadingCode] = useState(false);
  const [codeMessage, setCodeMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // Referral code at checkout
  const [referralCode, setReferralCode] = useState('');
  const [referralStatus, setReferralStatus] = useState<{
    valid: boolean;
    bonusGbPercentage: number;
    message: string;
  } | null>(null);
  const [isValidatingReferral, setIsValidatingReferral] = useState(false);
  // Debounce timer ref so we validate after user stops typing
  const referralDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Check for successful payment and track conversion
  useEffect(() => {
    const success = searchParams.get('success');
    
    if (success === 'true') {
      console.log('✅ Payment success detected - checking for conversion tracking');
      
      // Get payment details from localStorage (if stored)
      const pendingPayment = localStorage.getItem('pending_payment');
      
      if (pendingPayment) {
        try {
          const paymentData = JSON.parse(pendingPayment);
          const { priceUsd, orderId, gbAmount: paidGb } = paymentData;
          
          console.log('📊 Tracking purchase conversion:', { priceUsd, orderId, paidGb });
          
          // Track conversion with actual purchase value
          trackPurchaseConversion(priceUsd, 'USD', orderId);
          
          // Clear the pending payment from storage
          localStorage.removeItem('pending_payment');
        } catch (err) {
          console.error('❌ Error parsing pending payment:', err);
        }
      } else {
        // No stored payment data, but still track conversion (without value)
        console.log('⚠️ No payment data in localStorage, tracking conversion without value');
        trackPurchaseConversion(0); // Track without value
      }
      
      // Remove success parameter from URL (clean up)
      searchParams.delete('success');
      setSearchParams(searchParams, { replace: true });
      
      // Refresh user data to show new credits
      fetchUser();
    }
  }, [searchParams, setSearchParams, fetchUser]);

  // Tiered Pricing calculation
  const getPricePerGb = (gb: number): number => {
    if (gb >= 10000) return 0.70; // Enterprise: 10TB+ = $0.70/GB
    if (gb >= 1000) return 0.80;  // Growth: 1TB+ = $0.80/GB
    return 0.90;                  // Starter: any amount = $0.90/GB
  };

  const getTierName = (gb: number): string => {
    if (gb >= 10000) return 'Enterprise';
    if (gb >= 1000) return 'Growth';
    return 'Starter';
  };

  const getTierColor = (gb: number): string => {
    if (gb >= 10000) return 'text-pink-400';
    if (gb >= 1000) return 'text-purple-400';
    return 'text-blue-400';
  };

  const calculatePrice = (gb: number): number => {
    const pricePerGb = getPricePerGb(gb);
    const calculatedPrice = gb * pricePerGb;
    return Math.max(calculatedPrice, 10); // Minimum $10
  };

  const price = calculatePrice(gbAmount);
  const pricePerGb = getPricePerGb(gbAmount);
  const tierName = getTierName(gbAmount);
  const tierColor = getTierColor(gbAmount);
  const minGb = Math.ceil(10 / 0.9); // Minimum GB for $10

  // Handle referral code input changes — validate after 600ms of no typing
  const handleReferralCodeChange = (value: string) => {
    setReferralCode(value);
    setReferralStatus(null);

    // Clear previous debounce timer
    if (referralDebounceRef.current) clearTimeout(referralDebounceRef.current);

    const trimmed = value.trim().toUpperCase();
    if (!trimmed) return; // Empty — don't validate

    // Debounce: validate after user stops typing
    referralDebounceRef.current = setTimeout(async () => {
      setIsValidatingReferral(true);
      try {
        const result = await referralAPI.validateCode(trimmed);
        if (result.valid) {
          setReferralStatus({
            valid: true,
            bonusGbPercentage: result.bonusGbPercentage || 0,
            message: result.message || `+${result.bonusGbPercentage}% bonus GB applied!`
          });
        } else {
          setReferralStatus({
            valid: false,
            bonusGbPercentage: 0,
            message: result.error || 'Invalid referral code'
          });
        }
      } catch {
        setReferralStatus({ valid: false, bonusGbPercentage: 0, message: 'Could not validate code' });
      } finally {
        setIsValidatingReferral(false);
      }
    }, 600);
  };

  // Compute bonus GB if a valid referral code is applied
  const bonusGb = referralStatus?.valid
    ? Math.round(gbAmount * (referralStatus.bonusGbPercentage / 100))
    : 0;
  const totalGbWithBonus = gbAmount + bonusGb;

  const handleCreatePayment = async () => {
    if (gbAmount < minGb) {
      setError(`Minimum order is ${minGb}GB ($10)`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await authAPI.createCryptoPayment({
        gbAmount,
        priceUsd: price,
        // Pass referral code if one was validated successfully
        referralCode: referralStatus?.valid ? referralCode.trim().toUpperCase() : undefined
      });

      if (result.success && result.invoiceUrl) {
        // Store payment details in localStorage for conversion tracking
        localStorage.setItem('pending_payment', JSON.stringify({
          priceUsd: price,
          gbAmount: result.gbAmount || gbAmount, // use server-confirmed GB amount
          orderId: result.invoiceId || `order_${Date.now()}`,
          timestamp: Date.now()
        }));
        
        window.open(result.invoiceUrl, '_blank');
        setError(null);
      } else {
        setError(result.error || result.message || 'Failed to create payment');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRedeemCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!code.trim()) {
      setCodeMessage({ type: 'error', text: 'Please enter a code' });
      return;
    }

    setIsLoadingCode(true);
    setCodeMessage(null);

    try {
      const response = await authAPI.redeemCode(code.trim().toUpperCase());

      if (response.success) {
        setCodeMessage({ 
          type: 'success', 
          text: `Successfully added ${response.credits}MB to your account!` 
        });
        setCode('');
        fetchUser();
        setTimeout(() => {
          setShowCodeModal(false);
          setCodeMessage(null);
        }, 2000);
      } else {
        setCodeMessage({ type: 'error', text: response.message || 'Code redemption failed' });
      }
    } catch (error: any) {
      setCodeMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to redeem code. Please try again.' 
      });
    } finally {
      setIsLoadingCode(false);
    }
  };

  const quickAmounts = [
    { gb: 100, label: '100 GB' },
    { gb: 500, label: '500 GB' },
    { gb: 1000, label: '1 TB' },
    { gb: 5000, label: '5 TB' },
    { gb: 10000, label: '10 TB' },
  ];

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 max-w-6xl mx-auto">
        {/* Compact Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-white">Top Up Balance</h1>
            {user && (
              <p className="text-sm text-gray-400">
                Balance: <span className="text-blue-400 font-semibold">
                  {(Math.floor(user.credits / (1024 * 1024 * 1024) * 100) / 100).toFixed(2)} GB
                </span>
              </p>
            )}
          </div>
          <a
            href="https://t.me/NetloxProxies"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-medium transition-all text-sm whitespace-nowrap"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z"/>
            </svg>
            Help
          </a>
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          {/* Main Purchase Section */}
          <div className="lg:col-span-2 space-y-4">
            {/* Compact Pricing Display */}
            <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base md:text-lg font-semibold text-white">Select Amount</h2>
                <div className="text-xs text-gray-400">
                  <span className={`font-semibold ${tierColor}`}>{tierName}</span>
                  {' '}• ${pricePerGb.toFixed(2)}/GB
                </div>
              </div>
              
              {/* Quick Select Buttons */}
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-3">
                {quickAmounts.map((amount) => (
                  <button
                    key={amount.gb}
                    onClick={() => setGbAmount(amount.gb)}
                    className={`px-2 py-2 rounded-lg text-xs md:text-sm font-medium transition-all ${
                      gbAmount === amount.gb
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {amount.label}
                  </button>
                ))}
              </div>

              {/* Custom Amount Input */}
              <div className="relative">
                <input
                  type="number"
                  min={minGb}
                  step="1"
                  value={gbAmount}
                  onChange={(e) => setGbAmount(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white text-base font-semibold focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50"
                  disabled={loading}
                />
                <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 font-medium text-sm">
                  GB
                </span>
              </div>

              {gbAmount < minGb && (
                <p className="text-red-400 text-xs mt-2">
                  Minimum order: {minGb}GB ($10)
                </p>
              )}
            </div>

            {/* Compact Order Summary with Purchase Button */}
            <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
              <h2 className="text-base md:text-lg font-semibold text-white mb-3">Order Summary</h2>
              
              <div className="space-y-2">
                <div className="flex justify-between items-center py-1.5 border-b border-gray-700">
                  <span className="text-xs text-gray-400">Amount</span>
                  <span className="text-sm text-white font-semibold">{gbAmount.toLocaleString()} GB</span>
                </div>
                
                <div className="flex justify-between items-center py-1.5 border-b border-gray-700">
                  <span className="text-xs text-gray-400">Rate</span>
                  <span className="text-sm text-white font-semibold">${pricePerGb.toFixed(2)}/GB</span>
                </div>
                
                {gbAmount >= 1000 && (
                  <div className="flex justify-between items-center py-1.5 border-b border-gray-700">
                    <span className="text-xs text-gray-400">You Save</span>
                    <span className="text-sm text-green-400 font-semibold">
                      ${((0.90 - pricePerGb) * gbAmount).toFixed(2)}
                    </span>
                  </div>
                )}
                
                {/* Bonus GB row — only shown when a valid referral code is applied */}
                {bonusGb > 0 && (
                  <div className="flex justify-between items-center py-1.5 border-b border-gray-700">
                    <span className="text-xs text-green-400">Bonus GB (+{referralStatus?.bonusGbPercentage}%)</span>
                    <span className="text-sm text-green-400 font-semibold">+{bonusGb.toLocaleString()} GB</span>
                  </div>
                )}

                <div className="flex justify-between items-center pt-2">
                  <span className="text-base md:text-lg font-bold text-white">Total</span>
                  <div className="text-right">
                    <span className="text-xl md:text-2xl font-bold text-green-400">${price.toFixed(2)}</span>
                    {bonusGb > 0 && (
                      <p className="text-xs text-green-400">{totalGbWithBonus.toLocaleString()} GB credited</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Referral code input */}
              <div className="mt-4">
                <label className="block text-xs text-gray-400 mb-1.5">Referral Code (optional)</label>
                <div className="relative">
                  <input
                    type="text"
                    value={referralCode}
                    onChange={(e) => handleReferralCodeChange(e.target.value)}
                    placeholder="Enter code for bonus GB"
                    maxLength={20}
                    className={`w-full px-3 py-2 bg-gray-700 border rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none uppercase tracking-wider transition-colors ${
                      referralStatus?.valid
                        ? 'border-green-500 focus:border-green-400'
                        : referralStatus?.valid === false
                        ? 'border-red-500 focus:border-red-400'
                        : 'border-gray-600 focus:border-blue-500'
                    }`}
                    disabled={loading}
                  />
                  {/* Validation indicator */}
                  {isValidatingReferral && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">...</span>
                  )}
                  {!isValidatingReferral && referralStatus?.valid && (
                    <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                {/* Validation message */}
                {referralStatus && (
                  <p className={`text-xs mt-1 ${referralStatus.valid ? 'text-green-400' : 'text-red-400'}`}>
                    {referralStatus.message}
                  </p>
                )}
              </div>

              {error && (
                <div className="mt-3 p-2.5 bg-red-900/30 border border-red-500/30 rounded-lg">
                  <p className="text-red-400 text-xs">{error}</p>
                </div>
              )}

              <button
                onClick={handleCreatePayment}
                disabled={loading || gbAmount < minGb}
                className={`w-full mt-4 py-3 px-6 rounded-lg font-semibold text-base transition-all ${
                  loading || gbAmount < minGb
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-500 hover:to-blue-500 text-white shadow-lg hover:shadow-xl'
                }`}
              >
                {loading ? 'Processing...' : `Pay $${price.toFixed(2)} with Crypto`}
              </button>
            </div>
          </div>

          {/* Compact Sidebar */}
          <div className="space-y-4">
            {/* Pricing Tiers Info */}
            <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
              <h3 className="text-sm font-semibold text-white mb-3">Volume Pricing</h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center py-1.5">
                  <span className="text-xs text-gray-400">Starter</span>
                  <span className="text-sm font-bold text-blue-400">$0.90/GB</span>
                </div>
                <div className="flex justify-between items-center py-1.5">
                  <span className="text-xs text-gray-400">Growth (1TB+)</span>
                  <span className="text-sm font-bold text-purple-400">$0.80/GB</span>
                </div>
                <div className="flex justify-between items-center py-1.5">
                  <span className="text-xs text-gray-400">Enterprise (10TB+)</span>
                  <span className="text-sm font-bold text-pink-400">$0.70/GB</span>
                </div>
              </div>
            </div>

            {/* Payment Info */}
            <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
              <h3 className="text-sm font-semibold text-white mb-3">Payment Info</h3>
              <div className="space-y-2.5 text-xs">
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-white font-medium">300+ Cryptocurrencies</p>
                    <p className="text-gray-400">BTC, ETH, USDT & more</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-white font-medium">Instant Activation</p>
                    <p className="text-gray-400">Credits added immediately</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-white font-medium">Non-Expiring</p>
                    <p className="text-gray-400">Use at your own pace</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Redeem Code Button */}
            <button
              onClick={() => setShowCodeModal(true)}
              className="w-full bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-xl p-3.5 text-left transition-all"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-semibold text-sm">Have a code?</p>
                  <p className="text-gray-400 text-xs">Redeem top-up code</p>
                </div>
                <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          </div>
        </div>

        {/* Code Redemption Modal */}
        {showCodeModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl max-w-md w-full p-6 border border-gray-700">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-white">Redeem Code</h2>
                <button
                  onClick={() => {
                    setShowCodeModal(false);
                    setCodeMessage(null);
                    setCode('');
                  }}
                  className="text-gray-400 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleRedeemCode} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Enter your top-up code
                  </label>
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="XXXXX-XXXXX-XXXXX"
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase tracking-wider"
                    disabled={isLoadingCode}
                    maxLength={20}
                  />
                </div>

                {codeMessage && (
                  <div className={`p-3 rounded-lg ${
                    codeMessage.type === 'success' 
                      ? 'bg-green-900/50 border border-green-500 text-green-300' 
                      : 'bg-red-900/50 border border-red-500 text-red-300'
                  }`}>
                    <p className="text-sm">{codeMessage.text}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoadingCode || !code.trim()}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:from-gray-600 disabled:to-gray-600 disabled:cursor-not-allowed transition-all"
                >
                  {isLoadingCode ? 'Redeeming...' : 'Redeem Code'}
                </button>
              </form>

              <div className="mt-6 pt-6 border-t border-gray-700">
                <p className="text-sm text-gray-400 mb-2">Where to get codes:</p>
                <ul className="text-sm text-gray-500 space-y-1">
                  <li>• Purchase via Telegram bot</li>
                  <li>• Promotional codes from our team</li>
                  <li>• Special events and giveaways</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TopUpPage;
